import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const BRAND_GRADIENT = 'linear-gradient(135deg, #0047ff, #00c4ff)';

/* ================= BACKGROUND ================= */

const AnimatedBackground = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0; 
  overflow: hidden;
  background: radial-gradient(circle at top, #020617, #020617);
`;

const Blob = styled(motion.div)`
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.45;
`;

const BlobBlue = styled(Blob)`
  background: #0047ff;
  top: -120px;
  left: -120px;
`;

const BlobCyan = styled(Blob)`
  background: #00c4ff;
  bottom: -140px;
  right: -140px;
`;

/* ================= LAYOUT ================= */

const Wrapper = styled.div`
  position: relative;
  z-index: 1; 
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
  color: #e5e7eb;
  padding: 60px 16px 100px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

/* ================= TABS ================= */

const NavTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 50px;
  border-bottom: 1px solid rgba(255,255,255,0.15);
  padding-bottom: 12px;
  flex-wrap: wrap;
`;

const TabLink = styled(Link)`
  text-decoration: none;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 999px;
  color: #fff;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  backdrop-filter: blur(12px);
  transition: all 0.3s ease;

  &:hover {
    background: ${BRAND_GRADIENT};
    box-shadow: 0 0 18px rgba(0,196,255,0.45);
  }
`;

/* ================= HERO ================= */

const Hero = styled(motion.section)`
  text-align: center;
  margin-bottom: 80px;
`;

const Heading = styled.h1`
  font-size: clamp(2.2rem, 5vw, 3rem);
  margin-bottom: 20px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Paragraph = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  line-height: 1.6;
  color: #cbd5e1;
`;

const CtaButton = styled(motion.button)`
  padding: 14px 34px;
  border-radius: 50px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
`;

/* ================= CARDS ================= */

const Section = styled.section`
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-bottom: 80px;
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 420px;
  background: rgba(255,255,255,0.08);
  backdrop-filter: blur(14px);
  border-radius: 20px;
  padding: 28px;
  border: 1px solid rgba(255,255,255,0.15);
`;

const CardTitle = styled.h2`
  margin-bottom: 12px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: #cbd5e1;
`;

const Image = styled.img`
  width: 250px;
  max-width: 100%;
  border-radius: 16px;
  box-shadow: 0 12px 26px rgba(0,0,0,0.35);
`;

/* ================= COMPONENT ================= */

export default function Portfolio() {
  return (
    <>
      <AnimatedBackground>
        <BlobBlue
          animate={{ x: [0, 80, 0], y: [0, 60, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <BlobCyan
          animate={{ x: [0, -80, 0], y: [0, -60, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
      </AnimatedBackground>

      <Wrapper>
        <Container>

          <NavTabs>
            <TabLink to="/portfolio/projects">Our Projects</TabLink>
            <TabLink to="/portfolio/testimonials">Testimonials</TabLink>
          </NavTabs>

          <Hero
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Heading>Our Portfolio</Heading>
            <Paragraph>
              Explore our diverse portfolio of web and mobile applications, showcasing our unwavering
              commitment to overcoming challenges and driving success.
            </Paragraph>

            <CtaButton
              whileHover={{ scale: 1.08, boxShadow: '0 0 24px rgba(0,196,255,0.5)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.dispatchEvent(new Event('openQueryForm'))}
            >
              Request A Proposal
            </CtaButton>
          </Hero>

          {/* Portfolio Card 1 */}
          <Section>
            <Card whileHover={{ y: -8, boxShadow: '0 0 30px rgba(0,196,255,0.35)' }}>
              <CardTitle>Parinay Sanskar Mobile App</CardTitle>
              <CardDesc>
                Sathiya, a leading matrimony service provider, approached Berrybloomer with the goal of
                creating a mobile app to help users find ideal partners while safeguarding their sensitive data.
              </CardDesc>
            </Card>
            <Image src="/images/sathiya.png" alt="Sathiya App" />
          </Section>

          {/* Portfolio Card 2 */}
          <Section>
            <Card whileHover={{ y: -8, boxShadow: '0 0 30px rgba(0,196,255,0.35)' }}>
              <CardTitle>Desirely Mobile App</CardTitle>
              <CardDesc>
                Vivah approached Berrybloomer to modernize their matrimonial platform with a scalable,
                responsive, and engaging web architecture.
              </CardDesc>
            </Card>
            <Image src="/images/vivah.png" alt="Vivah App" />
          </Section>

        </Container>
      </Wrapper>
    </>
  );
}
